import React from 'react';
import parse from 'html-react-parser';

import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faRightToBracket, faRightFromBracket, faPaw } from '@fortawesome/free-solid-svg-icons';
import { IHotelDetails, IRoomsContent } from '@common-types';
import { PetsSvg, SmokingSvg } from '@assets';
import { getPropertyRules, IPropertyRule } from '@utils';

import './style.scss';

interface IProps {
  hotel: IHotelDetails;
  refAnchor: React.RefObject<HTMLDivElement>;
  /**
   * Whole-house stays state their check times, bedrooms and bathrooms in the
   * entire-home panel already, so this section keeps only what that panel does not
   * cover and presents it as one compact block.
   */
  isQuintess?: boolean;
  room?: IRoomsContent;
}

const FIRST = 0;
const PETS_RULE_ID = 'pets.allowed';

// The policies text comes straight from the backend (content provider), so we
// cannot rely on translation keys. Detect the well-known "Special instructions"
// phrase in every locale we support to pull it out into its own callout box.
// NOTE: if the content provider ever changes its wording, update the strings here.
const SPECIAL_INSTRUCTIONS_PHRASES = [
  'special instructions', // en
  'instrucciones especiales', // es
  'instructions spéciales', // fr
  'istruzioni speciali', // it
  'instruções especiais', // pt
];

// Strip HTML tags, collapse whitespace and lowercase so phrase matching is robust.
function toPlainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function isSpecialInstruction(html: string): boolean {
  const text = toPlainText(html);
  return SPECIAL_INSTRUCTIONS_PHRASES.some(phrase => text.startsWith(phrase));
}

class PoliciesComponent extends React.Component<IProps> {
  renderBox(icon: IconDefinition, titleId: string, items: string[]): React.ReactNode {
    return (
      <div className="policies__box">
        <div className="policies__box-header">
          <FontAwesomeIcon className="policies__box-icon" icon={icon} />
          <FormattedMessage id={titleId} />
        </div>
        {items.map((item, index) => (
          <div key={index} className="policies__info-text">
            {parse(item)}
          </div>
        ))}
      </div>
    );
  }

  /**
   * One compact block of only what the entire-home panel leaves out: the property
   * rules, and any policy line beyond the check times the panel already states.
   */
  renderGoodToKnow(rules: IPropertyRule[], notes: string[]): React.ReactNode {
    const { refAnchor } = this.props;

    if (!rules.length && !notes.length) {
      return null;
    }

    return (
      <div className="policies policies--compact" ref={refAnchor}>
        <h4 className="policies__title">
          <FormattedMessage id="good.to.know" />
        </h4>

        {!!rules.length && (
          <ul className="policies__rules">
            {rules.map(({ labelId, allowed }) => (
              <li key={labelId} className="policies__rule">
                {labelId === PETS_RULE_ID ? <PetsSvg /> : <SmokingSvg />}
                <span>
                  <FormattedMessage id={labelId} />: <FormattedMessage id={allowed ? 'yes' : 'no'} />
                </span>
              </li>
            ))}
          </ul>
        )}

        {!!notes.length && (
          <ul className="policies__notes">
            {notes.map((note, index) => (
              <li key={index} className="policies__note">
                {parse(note)}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  render(): React.ReactNode {
    const { hotel, refAnchor, isQuintess } = this.props;
    const { checkOutInformation = [], checkInInformation = [], pets = [], isVacationRental } = hotel;

    const checkIn = checkInInformation.filter(item => item !== null && item !== undefined);
    const checkOut = checkOutInformation.filter(item => item !== null && item !== undefined);
    const petsList = pets.filter(item => item !== null && item !== undefined);

    // "Special instructions" lines are shown as blue callout boxes at the bottom
    // instead of inside the check-in / check-out boxes. Dedupe identical lines so a
    // shared instruction (often repeated in both check-in and check-out) renders once.
    const specialInstructions: string[] = [];
    [...checkIn, ...checkOut].forEach(text => {
      if (isSpecialInstruction(text) && !specialInstructions.some(s => toPlainText(s) === toPlainText(text))) {
        specialInstructions.push(text);
      }
    });

    const checkInBox = checkIn.filter(text => !isSpecialInstruction(text));
    const checkOutBox = checkOut.filter(text => !isSpecialInstruction(text));

    if (isQuintess) {
      const { room } = this.props;
      const rules = getPropertyRules(room?.amenities, room);
      // The rules already say whether pets are welcome, so the prose saying the same
      // is dropped rather than stated twice in one block.
      const petsNotes = rules.some(({ labelId }) => labelId === PETS_RULE_ID) ? [] : petsList;
      // The panel states `[0]` of each array as the check time, so only what follows
      // it is new. Compared as plain text so a repeat of the same line drops out too.
      const stated = new Set(
        [checkIn[FIRST], checkOut[FIRST]].filter((text) => !!text).map(toPlainText),
      );
      const notes: string[] = [];
      // `importantInformation` is left out of the Overview above for these stays, since
      // an age limit or an ID requirement is the same kind of thing as the rest of this.
      const important = (hotel.importantInformation || []).filter((text) => !!text);

      [...checkIn, ...checkOut, ...petsNotes, ...important].forEach((text) => {
        const plain = toPlainText(text);

        if (!stated.has(plain) && !isSpecialInstruction(text)) {
          stated.add(plain);
          notes.push(text);
        }
      });

      return this.renderGoodToKnow(rules, [...notes, ...specialInstructions]);
    }

    const isCheckIn = !!checkInBox.length;
    const isCheckOut = !!checkOutBox.length;
    const isPets = !!petsList.length;
    const hasBoxes = isCheckIn || isCheckOut || isPets;
    const hasSpecialInstructions = !!specialInstructions.length;

    return (
      (hasBoxes || hasSpecialInstructions) && (
        <div className="policies" ref={refAnchor}>
          {
            isVacationRental
              ?
                <div className="rooms-search__list-text secondary-font vacation">
                  <div className="rooms-search__header secondary-font" style={{ fontSize: '24px' }}><FormattedMessage id="tab.policies" /></div>
                </div>
              : <h4 className="policies__title"><FormattedMessage id="tab.policies" /></h4>
          }

          {hasBoxes && (
            <div className="policies__boxes">
              {isCheckIn && this.renderBox(faRightToBracket, 'check.in', checkInBox)}
              {isCheckOut && this.renderBox(faRightFromBracket, 'check.out', checkOutBox)}
              {isPets && this.renderBox(faPaw, 'pets', petsList)}
            </div>
          )}

          {hasSpecialInstructions && (
            <div className="policies__special-instructions">
              {specialInstructions.map((text, index) => (
                <div key={index} className="policies__special-instruction">
                  {parse(text)}
                </div>
              ))}
            </div>
          )}
        </div>
      )
    );
  }
}

export const Policies = PoliciesComponent;
